import React from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowUpRight, ArrowDownRight, Package, ReceiptText, AlertTriangle, TrendingUp,
  Sparkles, ChevronRight, ChevronDown, CircleDollarSign, CalendarDays,
} from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip } from "recharts";
import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { PageContainer } from "@/components/layout/PageContainer";
import { useAuth, useWorkspace } from "@/lib/store";
import { productStockStatus } from "@/lib/formatters";
import { useLocalization } from "@/lib/localization";
import { hasPermission } from "@/lib/permissions";
import { formatOperatingCurrency } from "@/lib/money";
import { fetchExpenses } from "@/lib/shopApi";
import { parityCopy } from "@/lib/parityLocalization";

function startOfLocalDay(date) {
  return new Date(date.getFullYear(), date.getMonth(), date.getDate());
}

function addDays(date, days) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate() + days,
    date.getHours(),
    date.getMinutes(),
    date.getSeconds(),
    date.getMilliseconds()
  );
}

function parseSaleDate(sale) {
  const date = new Date(sale.createdAt ?? sale.createdAtIso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function sameLocalDay(first, second) {
  return first.getFullYear() === second.getFullYear() && first.getMonth() === second.getMonth() && first.getDate() === second.getDate();
}

function createDateRange(days = 1) {
  const today = startOfLocalDay(new Date());
  return { from: addDays(today, -(days - 1)), to: today };
}

function rangeBounds(range) {
  const from = startOfLocalDay(range.from);
  const inclusiveTo = startOfLocalDay(range.to || range.from);
  return { from, inclusiveTo, to: addDays(inclusiveTo, 1) };
}

function rangeDayCount(range) {
  const bounds = rangeBounds(range);
  return Math.max(1, Math.round((bounds.to.getTime() - bounds.from.getTime()) / 86400000));
}

function capitalizeFirst(value) {
  return value ? `${value.charAt(0).toLocaleUpperCase()}${value.slice(1)}` : value;
}

function formatConcreteRange(range, locale, includeWeekday = false) {
  const { from, inclusiveTo } = rangeBounds(range);
  const formatter = new Intl.DateTimeFormat(locale, {
    weekday: includeWeekday ? "long" : undefined,
    day: "numeric",
    month: includeWeekday ? "long" : "short",
    year: "numeric",
  });
  return sameLocalDay(from, inclusiveTo) ? formatter.format(from) : formatter.formatRange(from, inclusiveTo);
}

function formatHumanRange(range, locale) {
  const { from, inclusiveTo } = rangeBounds(range);
  if (sameLocalDay(from, inclusiveTo)) {
    const today = startOfLocalDay(new Date());
    const dayDifference = Math.round((from.getTime() - today.getTime()) / 86400000);
    if (dayDifference === 0 || dayDifference === -1) {
      return capitalizeFirst(new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(dayDifference, "day"));
    }
  }
  return formatConcreteRange(range, locale);
}

function isTodayRange(range) {
  const { from, inclusiveTo } = rangeBounds(range);
  const today = startOfLocalDay(new Date());
  return sameLocalDay(from, today) && sameLocalDay(inclusiveTo, today);
}

function buildRangeTrendPoints(sales, range, locale) {
  const bounds = rangeBounds(range);
  const totalDays = rangeDayCount(range);
  const daysPerBucket = totalDays <= 7 ? 1 : Math.ceil(totalDays / 7);
  const bucketCount = Math.ceil(totalDays / daysPerBucket);

  return Array.from({ length: bucketCount }, (_, index) => {
    const start = addDays(bounds.from, index * daysPerBucket);
    const end = new Date(Math.min(addDays(start, daysPerBucket).getTime(), bounds.to.getTime()));
    const labelDate = daysPerBucket === 1 ? start : addDays(end, -1);
    const total = sales.reduce((sum, sale) => {
      const occurredAt = parseSaleDate(sale);
      if (!occurredAt || occurredAt < start || occurredAt >= end) {
        return sum;
      }
      return sum + (sale.total || 0);
    }, 0);

    return {
      endIso: end.toISOString(),
      label:
        totalDays <= 7
          ? labelDate.toLocaleDateString(locale, { weekday: "short" })
          : labelDate.toLocaleDateString(locale, { month: "short", day: "numeric" }),
      startIso: start.toISOString(),
      total,
    };
  });
}

function buildTopProducts(sales) {
  const products = new Map();
  sales.forEach((sale) => {
    sale.items.forEach((item) => {
      const id = item.productId || item.name;
      const current = products.get(id) || { id, name: item.name, soldToday: 0 };
      current.soldToday += Number(item.quantity || 0);
      products.set(id, current);
    });
  });
  return [...products.values()].sort((first, second) => second.soldToday - first.soldToday).slice(0, 5);
}

function getDayGreetingHour() {
  return new Date().getHours();
}

function formatCurrencyLocal(value, locale) {
  return formatOperatingCurrency(value, locale);
}

function formatRelativeLocal(iso, locale) {
  if (!iso) return "—";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "—";

  const diffSeconds = Math.round((date.getTime() - Date.now()) / 1000);
  const absSeconds = Math.abs(diffSeconds);
  const formatter = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });

  if (absSeconds < 60) return formatter.format(diffSeconds, "second");
  if (absSeconds < 3600) return formatter.format(Math.round(diffSeconds / 60), "minute");
  if (absSeconds < 86400) return formatter.format(Math.round(diffSeconds / 3600), "hour");
  if (absSeconds < 604800) return formatter.format(Math.round(diffSeconds / 86400), "day");

  return date.toLocaleDateString(locale, { day: "2-digit", month: "short" });
}

function prepareRevenueChartData(points) {
  const hasRevenue = points.some((point) => (point.total || 0) > 0);
  if (!hasRevenue) {
    return points.map((point) => ({ ...point, displayTotal: 0 }));
  }

  const firstRevenueIndex = points.findIndex((point) => (point.total || 0) > 0);
  const lastRevenueIndex = points.reduce(
    (lastIndex, point, index) => ((point.total || 0) > 0 ? index : lastIndex),
    -1
  );

  return points.map((point, index) => ({
    ...point,
    displayTotal:
      (point.total || 0) > 0 || index < firstRevenueIndex || index > lastRevenueIndex
        ? point.total
        : null,
  }));
}

function isDebtPaid(status) {
  return status === "Paid";
}

export default function Dashboard() {
  const navigate = useNavigate();
  const { language, locale, messages } = useLocalization();
  const copy = messages.dashboard;
  const pulseCopy = parityCopy[language].pulse;
  const { session } = useAuth();
  const canAccessAssistant = hasPermission(session, "assistant");
  const canAccessBranches = hasPermission(session, "branch_management");
  const canAccessDebts = hasPermission(session, "debts");
  const canAccessExpenses = hasPermission(session, "expenses");
  const canAccessHistory = hasPermission(session, "history");
  const canAccessInventory = hasPermission(session, "inventory");
  const { products, currentBranch, debts, dashboard, analytics, sales } = useWorkspace();
  const [dateRange, setDateRange] = React.useState(() => createDateRange());
  const [pickerOpen, setPickerOpen] = React.useState(false);
  const [periodExpenses, setPeriodExpenses] = React.useState(0);

  const selectedBounds = React.useMemo(() => rangeBounds(dateRange), [dateRange]);
  const selectedDayCount = React.useMemo(() => rangeDayCount(dateRange), [dateRange]);
  const selectedRangeLabel = React.useMemo(() => formatHumanRange(dateRange, locale), [dateRange, locale]);
  const todaySelected = React.useMemo(() => isTodayRange(dateRange), [dateRange]);
  const periodSales = React.useMemo(() => sales
    .filter((sale) => {
      const occurredAt = parseSaleDate(sale);
      return occurredAt && occurredAt >= selectedBounds.from && occurredAt < selectedBounds.to;
    })
    .sort((first, second) => (parseSaleDate(second)?.getTime() || 0) - (parseSaleDate(first)?.getTime() || 0)), [sales, selectedBounds]);
  const periodTotal = periodSales.reduce((sum, sale) => sum + Number(sale.total || 0), 0);
  const saleCount = periodSales.length;
  const lowStockCount = dashboard.lowStockCount ?? products.filter((p) => productStockStatus(p) === "low_stock" || productStockStatus(p) === "stock_out").length;
  const outstandingDebt = debts.reduce((acc, d) => acc + (d.amountOwed - d.amountPaid), 0);
  const recent = React.useMemo(() => periodSales.slice(0, 5), [periodSales]);
  const topProducts = React.useMemo(() => buildTopProducts(periodSales), [periodSales]);
  const restockPlan = analytics.restockPlan;
  const greetingHour = getDayGreetingHour();
  const greeting = greetingHour < 12 ? copy.goodMorning : greetingHour < 17 ? copy.goodAfternoon : copy.goodEvening;
  const greetingName = session?.role ? `${session.role.toLowerCase()}` : "";
  const rawChartData = React.useMemo(() => buildRangeTrendPoints(periodSales, dateRange, locale), [dateRange, locale, periodSales]);
  const chartData = React.useMemo(() => prepareRevenueChartData(rawChartData), [rawChartData]);
  const chartRevenueTotal = rawChartData.reduce((a, b) => a + (b.total || 0), 0);
  const previousPeriodTotal = React.useMemo(() => {
    const previousFrom = addDays(selectedBounds.from, -selectedDayCount);
    return sales.reduce((sum, sale) => {
      const occurredAt = parseSaleDate(sale);
      return occurredAt && occurredAt >= previousFrom && occurredAt < selectedBounds.from ? sum + Number(sale.total || 0) : sum;
    }, 0);
  }, [sales, selectedBounds, selectedDayCount]);
  const revenueDeltaValue = previousPeriodTotal > 0
    ? Math.round(((periodTotal / previousPeriodTotal) - 1) * 100)
    : periodTotal > 0 ? 100 : 0;
  const revenueDelta = `${revenueDeltaValue > 0 ? "+" : ""}${revenueDeltaValue}%`;
  const stockOutCount = products.filter((p) => productStockStatus(p) === "stock_out").length;
  const expenseQueryRange = React.useMemo(() => ({ from: selectedBounds.from.toISOString(), to: selectedBounds.to.toISOString() }), [selectedBounds]);
  React.useEffect(() => {
    let active = true;
    setPeriodExpenses(0);
    if (!session?.sessionId || !canAccessExpenses) return () => { active = false; };
    void fetchExpenses(session.sessionId, expenseQueryRange)
      .then((response) => { if (active) setPeriodExpenses(Number(response?.expenseList?.total || 0)); })
      .catch(() => { if (active) setPeriodExpenses(0); });
    return () => { active = false; };
  }, [canAccessExpenses, currentBranch?.id, session?.sessionId, expenseQueryRange]);
  const productCosts = React.useMemo(() => new Map(products.map((product) => [product.id, Number(product.cost || 0)])), [products]);
  const hasTrackedCosts = periodSales.every((sale) => sale.items.every((item) => productCosts.has(item.productId) && productCosts.get(item.productId) > 0));
  const grossProfit = periodTotal - periodSales.reduce((sum, sale) => sum + sale.items.reduce((itemSum, item) => itemSum + (productCosts.get(item.productId) || 0) * item.quantity, 0), 0);
  const operatingProfit = grossProfit - periodExpenses;

  return (
    <PageContainer>
      <div className="mb-6">
        <div className="text-[10px] font-medium uppercase tracking-[0.28em] text-muted-foreground">
          {copy.businessPulseDate(
            formatConcreteRange(dateRange, locale, true),
            currentBranch?.name || ""
          )}
        </div>
        <div className="mt-3 flex flex-wrap items-end justify-between gap-4">
          <h1 className="text-[21px] lg:text-[25px] xl:text-[28px] font-semibold tracking-[-0.02em] leading-[1.08]">
            {greeting}
            {greetingName ? (
              <>
                {", "}
                <span className="font-editorial italic font-light text-primary capitalize">{greetingName}</span>
              </>
            ) : null}
            <span className="text-primary/70">.</span>
          </h1>
          <div className="flex items-center gap-2">
            <DashboardDateRangePicker
              locale={locale}
              open={pickerOpen}
              range={dateRange}
              rangeLabel={selectedRangeLabel}
              setOpen={setPickerOpen}
              onChange={setDateRange}
            />
            {canAccessAssistant ? <Button data-testid="dashboard-ask-ai" variant="outline" onClick={() => navigate("/assistant")} className="h-10 px-5">
              <Sparkles className="h-4 w-4 mr-1.5" /> {copy.askAI}
            </Button> : null}
          </div>
        </div>
        <div className="mt-3 h-px bg-gradient-to-r from-border via-border to-transparent" />
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-4 mb-6">
        <KpiCard
          testid="kpi-today-revenue"
          icon={<CircleDollarSign className="h-4 w-4" />}
          label={todaySelected ? copy.todayRevenue : copy.revenue}
          value={formatCurrencyLocal(periodTotal, locale)}
          delta={revenueDelta}
          deltaTone={revenueDeltaValue < 0 ? "down" : "up"}
          trend={chartData}
          variant="revenue"
          onClick={canAccessHistory ? () => navigate("/history") : undefined}
        />
        <KpiCard
          testid="kpi-sale-count"
          icon={<ReceiptText className="h-4 w-4" />}
          label={copy.salesCompleted}
          value={saleCount.toString()}
          delta={copy.recent(periodSales.length)}
          deltaTone="up"
          variant="sales"
          onClick={canAccessHistory ? () => navigate("/history") : undefined}
        />
        <KpiCard
          testid="kpi-low-stock"
          icon={<AlertTriangle className="h-4 w-4" />}
          label={copy.stockAlerts}
          value={lowStockCount.toString()}
          delta={copy.stockedOut(stockOutCount)}
          deltaTone="warn"
          variant="stock"
          onClick={canAccessInventory ? () => navigate(`/inventory?filter=${stockOutCount > 0 ? "stock_out" : "low_stock"}`) : undefined}
        />
        <KpiCard
          testid="kpi-outstanding-debt"
          icon={<Wallet className="h-4 w-4" />}
          label={copy.outstandingDebt}
          value={formatCurrencyLocal(outstandingDebt, locale)}
          delta={copy.customers(debts.filter((d) => !isDebtPaid(d.status)).length)}
          deltaTone="neutral"
          variant="debt"
          onClick={canAccessDebts ? () => navigate("/debts?filter=unpaid") : undefined}
        />
      </div>

      {canAccessExpenses ? (
        <button onClick={() => navigate("/expenses")} className="mb-6 grid w-full grid-cols-2 gap-px overflow-hidden rounded-2xl border bg-border text-left transition-colors hover:border-primary/25 md:grid-cols-4">
          <PulseMetric label={pulseCopy.revenue} value={formatCurrencyLocal(periodTotal, locale)} />
          <PulseMetric label={pulseCopy.grossProfit} value={hasTrackedCosts ? formatCurrencyLocal(grossProfit, locale) : pulseCopy.notTracked} />
          <PulseMetric label={pulseCopy.expenses} value={formatCurrencyLocal(periodExpenses, locale)} />
          <PulseMetric label={pulseCopy.operatingProfit} value={hasTrackedCosts ? formatCurrencyLocal(operatingProfit, locale) : pulseCopy.notTracked} accent />
        </button>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <div className="lg:col-span-2 luxe-card rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border/70 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">
                {copy.revenue} · {selectedRangeLabel}
              </div>
              <div className="numeral text-2xl lg:text-[28px] leading-none mt-2 text-foreground">
                {formatCurrencyLocal(chartRevenueTotal, locale)}
              </div>
            </div>
            <span className="rounded-full border border-primary/10 bg-primary/[0.055] px-2.5 py-1 text-[9px] font-semibold uppercase tracking-[0.14em] text-primary">{selectedDayCount}D</span>
          </div>
          <div className="p-3 h-72">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={chartData} margin={{ top: 16, right: 16, left: 4, bottom: 8 }}>
                <defs>
                  <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="hsl(var(--primary))" stopOpacity={0.35} />
                    <stop offset="100%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid vertical={false} stroke="hsl(var(--border))" strokeDasharray="3 6" opacity={0.7} />
                <XAxis
                  dataKey="label"
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  dy={8}
                />
                <YAxis
                  stroke="hsl(var(--muted-foreground))"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  width={44}
                  tickFormatter={(v) => formatCurrencyLocal(v, locale)}
                />
                <RTooltip
                  filterNull
                  cursor={{ stroke: "hsl(var(--border))" }}
                  contentStyle={{ background: "hsl(var(--popover))", border: "1px solid hsl(var(--border))", borderRadius: 10, fontSize: 12, boxShadow: "var(--shadow-lift)" }}
                  formatter={(v) => [formatCurrencyLocal(v, locale), copy.revenue]}
                />
                <Area
                  dataKey="displayTotal"
                  connectNulls
                  stroke="hsl(var(--primary))"
                  strokeWidth={2.4}
                  fill="url(#revGrad)"
                  dot={{ r: 3, strokeWidth: 2, fill: "hsl(var(--background))" }}
                  activeDot={{ r: 5, strokeWidth: 2, fill: "hsl(var(--primary))" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="luxe-card rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border/70 flex items-center justify-between">
            <div>
              <div className="text-[10px] uppercase tracking-[0.22em] text-muted-foreground font-semibold">{copy.topMovers}</div>
              <div className="text-sm font-medium mt-1">{todaySelected ? copy.topMoversMeta : selectedRangeLabel}</div>
            </div>
            <TrendingUp className="h-4 w-4 text-success" />
          </div>
          <div className="px-4 py-3 space-y-2.5 h-56 overflow-hidden">
            {topProducts.map((p, i) => {
              const max = Math.max(...topProducts.map((x) => x.soldToday || 1));
              const pct = Math.round(((p.soldToday || 0) / max) * 100);
              return (
                <button
                  key={p.id}
                  onClick={canAccessInventory ? () => navigate(`/inventory/${encodeURIComponent(p.id)}?q=${encodeURIComponent(p.name)}`) : undefined}
                  disabled={!canAccessInventory}
                  className="group block w-full text-left rounded-lg px-2 py-1.5 hover:bg-accent/30 transition-colors"
                >
                  <div className="flex items-baseline justify-between gap-2 mb-1.5">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="font-mono text-[10px] text-muted-foreground w-3">{i + 1}</span>
                      <span className="text-xs font-medium truncate">{p.name}</span>
                    </div>
                    <span className="font-mono text-xs font-semibold tabular-nums shrink-0">{p.soldToday}</span>
                  </div>
                  <div className="h-1 rounded-full bg-muted/70 overflow-hidden">
                    <div
                      className="h-full rounded-full bg-gradient-to-r from-primary/70 to-primary transition-all duration-700"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                </button>
              );
            })}
            {topProducts.length === 0 && (
              <div className="h-full grid place-items-center text-sm text-muted-foreground">
                {copy.noProductMovementYet}
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        <InsightCard
          tone="primary"
          tag={copy.liveInsight}
          title={topProducts[0] ? (todaySelected ? copy.topProductLead(topProducts[0].name) : topProducts[0].name) : copy.salesSyncing}
          body={topProducts[0]
            ? (todaySelected ? copy.topProductLeadBody(topProducts[0].soldToday || 0) : `${topProducts[0].soldToday || 0} ${copy.units} · ${selectedRangeLabel}`)
            : copy.noTopProductData}
          cta={canAccessInventory ? copy.viewProduct : null}
          onClick={canAccessInventory ? () => navigate("/inventory") : undefined}
        />
        <InsightCard
          tone="warning"
          tag={copy.actionRecommended}
          title={restockPlan[0] ? copy.restockToday(restockPlan[0].productName) : copy.restockPlanReview}
          body={restockPlan[0]
            ? copy.restockBody(restockPlan[0].reason, restockPlan[0].recommendedQuantity)
            : copy.restockAnalyticsEmpty}
          cta={copy.openAnalytics}
          onClick={() => navigate("/analytics")}
        />
        <InsightCard
          tone="muted"
          tag={copy.branch}
          title={currentBranch?.name ? copy.activeBranch(currentBranch.name) : copy.reviewBranches}
          body={currentBranch?.id
            ? copy.viewWorkspaceFor(currentBranch.name)
            : copy.noBranchSelected}
          cta={canAccessBranches ? copy.reviewBranches : null}
          onClick={canAccessBranches ? () => navigate("/branches") : undefined}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 luxe-card rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border/70 flex items-center justify-between">
            <div>
              <div className="text-sm font-semibold tracking-tight">{copy.recentSales}</div>
              <div className="text-xs text-muted-foreground mt-0.5">{copy.recentSalesMeta(recent.length, currentBranch?.name || "")}</div>
            </div>
            {canAccessHistory ? <Button data-testid="dashboard-view-history" variant="ghost" size="sm" onClick={() => navigate("/history")} className="text-xs">
              {copy.viewAll} <ChevronRight className="h-3.5 w-3.5 ml-1" />
            </Button> : null}
          </div>
          <div>
            {recent.map((s) => (
              <button
                key={s.id}
                onClick={canAccessHistory ? () => navigate(`/history?saleId=${encodeURIComponent(s.id)}`) : undefined}
                disabled={!canAccessHistory}
                className="w-full text-left px-5 py-3.5 border-b border-border/60 last:border-b-0 flex items-center gap-3.5 hover:bg-accent/30 transition-colors"
              >
                <div className="h-9 w-9 rounded-lg bg-muted/60 ring-1 ring-border/60 flex items-center justify-center">
                  <Package className="h-4 w-4 text-muted-foreground" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">
                    {s.items.map((i) => `${i.quantity}× ${i.name}`).join(", ")}
                  </div>
                  <div className="text-xs text-muted-foreground mt-0.5">
                    {s.cashier} · {formatRelativeLocal(s.createdAt, locale)}
                  </div>
                </div>
                <PaymentStatusBadge copy={copy} status={s.paymentStatus} />
                <div className="font-mono text-sm font-semibold tabular-nums w-24 text-right">
                  {formatCurrencyLocal(s.total, locale)}
                </div>
              </button>
            ))}
            {recent.length === 0 && (
              <div className="px-5 py-10 text-sm text-muted-foreground">{copy.noRecentSalesYet}</div>
            )}
          </div>
        </div>

        <div className="luxe-card rounded-2xl overflow-hidden">
          <div className="px-5 py-4 border-b border-border/70">
            <div className="text-sm font-semibold tracking-tight">{copy.restockPlan}</div>
            <div className="text-xs text-muted-foreground mt-0.5">{copy.liveAnalyticsRecommendations}</div>
          </div>
          <div className="divide-y divide-border/60">
            {restockPlan.map((r) => (
              <button
                key={r.productId}
                onClick={canAccessInventory ? () => navigate(`/inventory/${encodeURIComponent(r.productId)}?q=${encodeURIComponent(r.productName)}`) : undefined}
                disabled={!canAccessInventory}
                className="w-full text-left px-5 py-3.5 flex items-center gap-3 hover:bg-accent/30 transition-colors"
              >
                <PriorityDot p={r.priority} />
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium truncate">{r.productName}</div>
                  <div className="text-xs text-muted-foreground truncate">{r.reason}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-sm font-semibold tabular-nums">+{r.recommendedQuantity}</div>
                  <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{copy.units}</div>
                </div>
              </button>
            ))}
            {restockPlan.length === 0 && (
              <div className="px-5 py-10 text-sm text-muted-foreground">{copy.noRestockRecommendationsYet}</div>
            )}
          </div>
        </div>
      </div>
    </PageContainer>
  );
}

function DashboardDateRangePicker({ locale, open, range, rangeLabel, setOpen, onChange }) {
  const todayLabel = capitalizeFirst(new Intl.RelativeTimeFormat(locale, { numeric: "auto" }).format(0, "day"));

  function selectPreset(days) {
    onChange(createDateRange(days));
    setOpen(false);
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          data-testid="dashboard-date-range"
          variant="outline"
          className="group h-10 min-w-[176px] justify-start rounded-xl border-primary/15 bg-primary/[0.07] px-2.5 shadow-sm hover:border-primary/25 hover:bg-primary/[0.07]"
        >
          <span className="mr-2.5 grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-primary text-primary-foreground shadow-[0_8px_18px_-10px_hsl(var(--primary))]">
            <CalendarDays className="h-3.5 w-3.5" />
          </span>
          <span className="min-w-0 flex-1 truncate text-left text-xs font-semibold">{rangeLabel}</span>
          <ChevronDown className="ml-2 h-3.5 w-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" sideOffset={8} className="w-auto overflow-hidden rounded-[20px] border-border/80 bg-popover p-0 shadow-[0_28px_70px_-28px_rgba(15,23,42,0.42)]">
        <div className="flex items-center justify-between gap-4 border-b border-border/70 bg-gradient-to-r from-primary/[0.055] via-card to-card px-4 py-3">
          <div>
            <div className="text-[9px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">{rangeLabel}</div>
            <div className="mt-0.5 text-xs font-medium">{formatConcreteRange(range, locale)}</div>
          </div>
          <div className="flex items-center gap-1 rounded-xl border border-border/70 bg-background/70 p-1">
            <RangePreset active={rangeDayCount(range) === 1 && isTodayRange(range)} onClick={() => selectPreset(1)}>{todayLabel}</RangePreset>
            <RangePreset active={rangeDayCount(range) === 7} onClick={() => selectPreset(7)}>7D</RangePreset>
            <RangePreset active={rangeDayCount(range) === 30} onClick={() => selectPreset(30)}>30D</RangePreset>
          </div>
        </div>
        <Calendar
          mode="range"
          defaultMonth={range.from}
          selected={range}
          onSelect={(nextRange) => {
            if (!nextRange?.from) return;
            const normalizedRange = {
              from: startOfLocalDay(nextRange.from),
              to: startOfLocalDay(nextRange.to || nextRange.from),
            };
            onChange(normalizedRange);
            if (nextRange.to && !sameLocalDay(nextRange.from, nextRange.to)) setOpen(false);
          }}
          disabled={{ after: new Date() }}
          numberOfMonths={2}
          initialFocus
          className="p-4"
        />
      </PopoverContent>
    </Popover>
  );
}

function RangePreset({ active, children, onClick }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`h-7 rounded-lg px-2.5 text-[10px] font-semibold transition-colors ${active ? "bg-primary text-primary-foreground shadow-sm" : "text-muted-foreground hover:bg-muted hover:text-foreground"}`}
    >
      {children}
    </button>
  );
}

function PulseMetric({ label, value, accent = false }) {
  return <div className="bg-card px-4 py-3.5"><div className="text-[9px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{label}</div><div className={`mt-1.5 truncate text-sm font-semibold tabular-nums ${accent ? "text-primary" : ""}`}>{value}</div></div>;
}

function Wallet(props) {
  return (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" {...props}>
      <path d="M20 12V8H6a2 2 0 0 1 0-4h12v4" />
      <path d="M4 6v12a2 2 0 0 0 2 2h14v-4" />
      <path d="M18 12a2 2 0 0 0 0 4h4v-4Z" />
    </svg>
  );
}

function KpiCard({ icon, label, value, delta, deltaTone = "up", trend, variant = "sales", testid, onClick }) {
  const toneMap = {
    up: "text-success",
    down: "text-destructive",
    warn: "text-warning",
    neutral: "text-muted-foreground",
  };
  const Trend = deltaTone === "down" ? ArrowDownRight : ArrowUpRight;
  const isRevenue = variant === "revenue";
  const cardStyles = {
    revenue: "border-emerald-800/40 bg-[linear-gradient(135deg,#063f38_0%,#0a5b4e_55%,#087060_100%)] shadow-[0_18px_45px_-24px_rgba(6,78,65,0.8)]",
    sales: "border-primary/20 bg-gradient-to-br from-card via-card to-primary/[0.06]",
    stock: "border-warning/25 bg-gradient-to-br from-card via-card to-warning/[0.07]",
    debt: "border-border bg-gradient-to-br from-card via-card to-slate-500/[0.07]",
  };
  const iconStyles = {
    revenue: "bg-white/12 text-emerald-50 ring-white/15",
    sales: "bg-primary/10 text-primary ring-primary/15",
    stock: "bg-warning/10 text-warning ring-warning/20",
    debt: "bg-slate-500/10 text-slate-500 dark:text-slate-300 ring-slate-500/15",
  };
  const deltaStyles = isRevenue
    ? "border-white/15 bg-white/10 text-emerald-50"
    : `${toneMap[deltaTone]} border-border/70 bg-background/55`;
  const sparklineId = `spk-${testid}`;

  return (
    <button
      data-testid={testid}
      onClick={onClick}
      disabled={!onClick}
      className={`luxe-card bg-grain group min-h-[152px] w-full rounded-[20px] overflow-hidden text-left ${onClick ? "luxe-card-hover" : "cursor-default"} ${cardStyles[variant]}`}
    >
      {isRevenue ? (
        <>
          <svg className="absolute inset-0 h-full w-full opacity-[0.16]" aria-hidden>
            <defs>
              <pattern id="revenue-pattern" width="48" height="48" patternUnits="userSpaceOnUse" patternTransform="rotate(35)">
                <path d="M 0 24 H 48" fill="none" stroke="white" strokeWidth="1" />
                <circle cx="24" cy="24" r="3" fill="none" stroke="white" strokeWidth="1" />
              </pattern>
              <linearGradient id="revenue-fade" x1="0" y1="0" x2="1" y2="0">
                <stop offset="0%" stopColor="white" stopOpacity="0" />
                <stop offset="100%" stopColor="white" stopOpacity="1" />
              </linearGradient>
              <mask id="revenue-mask"><rect width="100%" height="100%" fill="url(#revenue-fade)" /></mask>
            </defs>
            <rect width="100%" height="100%" fill="url(#revenue-pattern)" mask="url(#revenue-mask)" />
          </svg>
          <div className="absolute -right-12 -top-14 h-44 w-44 rounded-full bg-emerald-300/15 blur-3xl" />
        </>
      ) : (
        <div className={`absolute -right-14 -top-16 h-40 w-40 rounded-full blur-3xl ${variant === "stock" ? "bg-warning/10" : variant === "debt" ? "bg-slate-400/10" : "bg-primary/10"}`} />
      )}
      <div className="flex h-full flex-col p-3.5">
        <div className={`flex items-center justify-between gap-3 ${isRevenue ? "text-emerald-50/80" : "text-muted-foreground"}`}>
          <div className="flex items-center gap-2.5">
            <span className={`grid h-8 w-8 place-items-center rounded-xl ring-1 ring-inset ${iconStyles[variant]}`}>{icon}</span>
            <span className="text-[9px] uppercase tracking-[0.19em] font-semibold">{label}</span>
          </div>
          <span className={`h-1.5 w-1.5 rounded-full ${isRevenue ? "bg-emerald-200 shadow-[0_0_0_3px_rgba(167,243,208,0.12)]" : variant === "stock" ? "bg-warning" : "bg-primary/70"}`} />
        </div>
        <div className="mt-3.5 flex items-end justify-between gap-2.5">
          <div className={`numeral text-[23px] lg:text-[26px] leading-none tracking-[-0.032em] ${isRevenue ? "text-white" : "text-foreground"}`}>
            {value}
          </div>
          {trend && (
            <div className="h-8 w-[4.5rem] -mb-1 opacity-90">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={trend} margin={{ top: 2, right: 0, left: 0, bottom: 0 }}>
                  <defs>
                    <linearGradient id={sparklineId} x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#a7f3d0" stopOpacity={0.45} />
                      <stop offset="100%" stopColor="#a7f3d0" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <Area dataKey="total" stroke="#a7f3d0" strokeWidth={1.8} fill={`url(#${sparklineId})`} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
        <div className={`mt-auto w-fit inline-flex items-center gap-1 rounded-full border px-2.5 py-[0.3rem] text-[10px] font-semibold ${deltaStyles}`}>
          {(deltaTone === "up" || deltaTone === "down") && <Trend className="h-3 w-3" />}
          {delta}
        </div>
      </div>
    </button>
  );
}

function InsightCard({ tone = "primary", tag, title, body, cta, onClick }) {
  const styles = {
    primary: "border-primary/25 bg-gradient-to-br from-primary/[0.06] via-card to-card",
    warning: "border-warning/35 bg-gradient-to-br from-warning/[0.08] via-card to-card",
    muted: "border-border bg-card",
  };
  const tagStyles = {
    primary: "bg-primary text-primary-foreground",
    warning: "bg-warning text-warning-foreground",
    muted: "bg-foreground/85 text-background",
  };
  return (
    <div className={`luxe-card luxe-card-hover bg-grain rounded-2xl border ${styles[tone]} p-5 flex flex-col`}>
      <div className="flex items-center gap-2 mb-3.5">
        <span className={`text-[9px] font-bold uppercase tracking-[0.18em] px-2.5 py-1 rounded-full ${tagStyles[tone]}`}>
          {tag}
        </span>
      </div>
      <div className="text-[16px] font-semibold tracking-[-0.01em] leading-snug text-balance">{title}</div>
      <div className="text-sm text-muted-foreground mt-2 flex-1 leading-relaxed">{body}</div>
      {cta && onClick ? <button onClick={onClick} className="mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-foreground hover:text-primary transition-colors group w-fit">
        {cta}
        <ChevronRight className="h-3.5 w-3.5 group-hover:translate-x-0.5 transition-transform" />
      </button> : null}
    </div>
  );
}

function PaymentStatusBadge({ copy, status }) {
  const map = {
    Paid: { className: "bg-success/15 text-success", label: copy.paymentPaid },
    "Partly paid": { className: "bg-warning/15 text-warning", label: copy.paymentPartlyPaid },
    Unpaid: { className: "bg-destructive/15 text-destructive", label: copy.paymentUnpaid },
  };
  const state = map[status] || { className: "bg-muted text-muted-foreground", label: status };
  return <span className={`text-[10px] font-semibold uppercase tracking-wider px-2 py-1 rounded-full ${state.className}`}>{state.label}</span>;
}

function PriorityDot({ p }) {
  const c = p === "high" ? "bg-destructive" : p === "medium" ? "bg-warning" : "bg-success";
  return <span className={`h-2 w-2 rounded-full ${c} shrink-0`} />;
}
